// esriJSON geometry to GeoJSON, honouring ring winding (BBB-454).
//
// WHY THIS EXISTS. Most ArcGIS services convert their own geometry correctly when
// asked for `f=geojson`, so the extractor asks for that and does no conversion.
// The Natural England Provisional ALC service does not: it emits every ring as a
// separate outer part and discards the interior rings entirely.
//
// Measured live on 20/08/2026, OBJECTID 971, the Grade 3 polygon:
//   esriJSON  113 rings, 27 clockwise (outer) and 86 counter-clockwise (holes)
//   geojson   113 outer parts, 0 holes
//   turf area 2,915 km² against the service's own Shape__Area of 1,933 km²
//
// The consequence a farmer saw was the Agricultural Land Classification make-up
// totalling more than 100%: Grade 3 filled the holes where the Grade 2 and Grade 4
// land actually sits, so it claimed 100% of a field that also read 75% Grade 2.
// Cross-checked at the time: the two hole-free grades measured within projection
// tolerance and overlapped each other by exactly zero, so only the holed grade
// bled. England & Wales Soilscapes was checked over the same area and preserved
// all 111 of its holes, so this is one badly behaved service and NOT a general
// fault in the extractor. Hence a per-layer opt-in rather than a global switch.
//
// esriJSON has no explicit outer/hole flag. Winding order is the only signal:
// clockwise is an outer ring and counter-clockwise is a hole. That information
// survives in `f=json`, which is why the fix is a conversion rather than a repair.

import { booleanPointInPolygon } from "@turf/boolean-point-in-polygon";

/**
 * Shoelace signed area of a ring, in whatever units the coordinates are in.
 * Positive means counter-clockwise, negative means clockwise. Only the sign and
 * the relative magnitude are used, so the planar approximation is fine here.
 */
function signedArea(ring) {
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    sum += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
  }
  return sum / 2;
}

/**
 * Close a ring if the server left it open, and reject anything that cannot
 * describe an area. A ring needs three distinct points, so four once closed.
 */
function normaliseRing(ring) {
  if (!Array.isArray(ring) || ring.length < 3) return null;

  const closed = ring.slice();
  const [firstX, firstY] = closed[0];
  const [lastX, lastY] = closed[closed.length - 1];
  if (firstX !== lastX || firstY !== lastY) closed.push([firstX, firstY]);

  if (closed.length < 4) return null;
  return closed;
}

/** A ring wound the other way. GeoJSON and esriJSON disagree on direction. */
function reverse(ring) {
  return ring.slice().reverse();
}

/**
 * A point to test containment with. The first vertex is used because a hole's
 * vertices lie strictly inside its containing outer ring in every real dataset,
 * and it costs nothing to compute.
 */
function representativePoint(ring) {
  return { type: "Point", coordinates: ring[0] };
}

/**
 * esriJSON rings to a GeoJSON Polygon or MultiPolygon.
 *
 * Clockwise rings are outer; counter-clockwise rings are holes and are attached
 * to the SMALLEST outer ring that contains them, which is what makes nested
 * outers behave. A counter-clockwise ring with no container is promoted to an
 * outer ring rather than dropped: dropping it would silently lose land, and
 * losing area is the bug this module fixes.
 *
 * Output follows RFC 7946: exterior rings counter-clockwise, holes clockwise.
 *
 * @param {Array<Array<Array<number>>>} rings esriJSON `geometry.rings`
 * @returns {{type: string, coordinates: Array}|null} null when nothing usable
 */
export function esriRingsToGeoJson(rings) {
  if (!Array.isArray(rings) || rings.length === 0) return null;

  const outers = [];
  const holes = [];

  for (const raw of rings) {
    const ring = normaliseRing(raw);
    if (!ring) continue;

    const signed = signedArea(ring);
    // A zero-area ring is a sliver or a repeated point. It can neither be a part
    // nor a hole, and feeding it to point-in-polygon is asking for trouble.
    if (signed === 0) continue;

    if (signed < 0) outers.push({ ring, size: Math.abs(signed) });
    else holes.push({ ring, size: Math.abs(signed) });
  }

  // Every ring wound the same way as a hole and nothing to put them in. Treat
  // them as parts so the geometry still covers the ground it should.
  if (outers.length === 0) {
    if (holes.length === 0) return null;
    const parts = holes.map((h) => [h.ring]);
    return parts.length === 1
      ? { type: "Polygon", coordinates: parts[0] }
      : { type: "MultiPolygon", coordinates: parts };
  }

  // Smallest first, so the first containing outer found is the tightest one and
  // a hole inside nested outers lands in the inner part rather than the outer.
  const bySize = outers.slice().sort((a, b) => a.size - b.size);
  const assigned = new Map(outers.map((o) => [o, []]));
  const orphans = [];

  for (const hole of holes) {
    const point = representativePoint(hole.ring);
    let container = null;

    for (const candidate of bySize) {
      // Only the outer ring itself matters for containment; its own holes are
      // still being worked out.
      const polygon = { type: "Polygon", coordinates: [candidate.ring] };
      let inside = false;
      try {
        inside = booleanPointInPolygon(point, polygon);
      } catch {
        // Degenerate ring the library will not accept. Try the next candidate
        // rather than losing the hole to one bad part.
        inside = false;
      }
      if (inside) {
        container = candidate;
        break;
      }
    }

    if (container) assigned.get(container).push(hole.ring);
    else orphans.push(hole.ring);
  }

  const parts = outers.map((outer) => [
    // Esri outer rings are clockwise; RFC 7946 wants exteriors counter-clockwise.
    reverse(outer.ring),
    // Esri holes are counter-clockwise; RFC 7946 wants interiors clockwise.
    ...assigned.get(outer).map(reverse),
  ]);

  // Orphans are already counter-clockwise, which is the exterior winding, so
  // they go in as they are.
  for (const orphan of orphans) parts.push([orphan]);

  return parts.length === 1
    ? { type: "Polygon", coordinates: parts[0] }
    : { type: "MultiPolygon", coordinates: parts };
}

/**
 * One esriJSON feature to a GeoJSON Feature.
 *
 * @param {{attributes?: object, geometry?: {rings?: Array}}} feature
 * @returns {{type: string, properties: object, geometry: object}|null} null when
 *   the feature carries no usable geometry, so callers can skip it the same way
 *   they skip a feature that fails to clip.
 */
export function esriFeatureToGeoJson(feature) {
  if (!feature) return null;

  const geometry = esriRingsToGeoJson(feature.geometry?.rings);
  if (!geometry) return null;

  return {
    type: "Feature",
    properties: feature.attributes || {},
    geometry,
  };
}
